import { GoogleGenerativeAI } from '@google/generative-ai';

// Initialize the Gemini API
const API_KEY = 'AIzaSyDylvpBvJdHs0CsX7trGi_0Rt1p8smJt6U' 
//process.env.REACT_APP_GEMINI_API_KEY;
if (!API_KEY) {
  console.error('REACT_APP_GEMINI_API_KEY is not set in environment variables');
}

const genAI = new GoogleGenerativeAI(API_KEY || '');

export interface TaskSuggestion {
  title: string;
  description: string;
  quadrant: number;
}

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export const analyzeTask = async (taskDescription: string): Promise<TaskSuggestion> => {
  let lastError: Error | null = null;
  
  for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
    try {
      if (!API_KEY) {
        throw new Error('Gemini API key is not configured');
      }

      console.log(`Initializing Gemini model... (Attempt ${attempt}/${MAX_RETRIES})`);
      const model = genAI.getGenerativeModel({ model: 'gemini-2.0-flash' });

      const prompt = `Analyze the following task and suggest how it should be categorized in the Eisenhower Matrix (1-4).
      Consider both the title and description when determining the quadrant.
      
      Task: ${taskDescription}
      
      Please respond in the following JSON format:
      {
        "title": "Suggested task title (keep it concise and action-oriented)",
        "description": "Detailed task description with specific steps, deadlines, and success criteria",
        "quadrant": number (1-4)
      }
      
      Quadrant meanings:
      1. Important & Urgent - Tasks that need immediate attention and have significant consequences
      2. Important & Not Urgent - Tasks that contribute to long-term goals and personal development
      3. Not Important & Urgent - Tasks that are time-sensitive but don't contribute to long-term goals
      4. Not Important & Not Urgent - Tasks that are neither urgent nor important, often distractions
      
      Consider the following when categorizing:
      - Deadline and time sensitivity (immediate, short-term, long-term)
      - Impact on goals and objectives (high, medium, low)
      - Consequences of not doing the task (severe, moderate, minimal)
      - Long-term value and importance (strategic, tactical, trivial)
      - Dependencies and prerequisites
      - Resource requirements and availability
      
      Guidelines for task titles:
      - Start with an action verb
      - Be specific and measurable
      - Keep it concise (5-7 words)
      
      Guidelines for descriptions:
      - Include specific steps or subtasks
      - Add relevant deadlines or timeframes
      - Specify success criteria
      - Note any dependencies or prerequisites
      - Include resource requirements if applicable`;

      console.log('Sending request to Gemini API...');
      const result = await model.generateContent(prompt);
      const response = await result.response;
      const text = response.text();
      
      console.log('Received response from Gemini API:', text);
      
      // Remove code block markers if present
      const cleaned = text.replace(/```json|```/g, '').trim();
      
      try {
        const suggestion = JSON.parse(cleaned) as TaskSuggestion;
        
        // Validate the suggestion
        if (!suggestion.title || !suggestion.description || !suggestion.quadrant) {
          throw new Error('Invalid suggestion format: missing required fields');
        }
        
        if (suggestion.quadrant < 1 || suggestion.quadrant > 4) {
          throw new Error('Invalid quadrant number: must be between 1 and 4');
        }
        
        return suggestion;
      } catch (parseError) {
        console.error('Error parsing Gemini response:', parseError);
        throw new Error('Failed to parse Gemini response into valid task suggestion');
      }
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));
      console.error(`Error analyzing task with Gemini (Attempt ${attempt}/${MAX_RETRIES}):`, lastError);
      
      if (attempt < MAX_RETRIES) {
        console.log(`Retrying in ${RETRY_DELAY}ms...`);
        await sleep(RETRY_DELAY);
      }
    }
  }
  
  throw lastError || new Error('Failed to analyze task after multiple attempts');
}; 